import { useCountByStatus } from "@/api/auction/auction";
import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import {
  Exhibition,
  Multipleprint,
  MultipleprintInExhibition,
} from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import MultipleprintInExhibitionDrawer from "./drawer/MultipleprintInExhibitionDrawer";

interface ComplexLinkProps {
  id: string;
  multipleprint: Multipleprint;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

type MultipleprintInExhibitionLinksProps =
  LinksTableProps<MultipleprintInExhibition> & {
    multipleprint: Multipleprint;
  };

/**
 * MultipleprintInExhibtionLinks component
 * @returns {JSX.Element}
 */
const MultipleprintInExhibtionLinks = ({
  id,
  multipleprint,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleprintInExhibition",
    type,
  );

  const { mutate: deleteMultipleprintInExhibition } = useDeleteComplexLink(
    id,
    "multipleprintInExhibition",
    type,
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteMultipleprintInExhibition({
        id,
        relation: "multipleprintInExhibition",
      });
    },
    [deleteMultipleprintInExhibition],
  );

  const columns: ColumnDef<MultipleprintInExhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "exhibition.expositionsDescriptionNumeroExpo",
        header: t("expositionsDescriptionNumeroExpo"),
        cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => {
          return (
            <TextCell
              value={row.original.exhibition.expositionsDescriptionNumeroExpo}
            />
          );
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"exhibition"}
            />
          );
        },
      },
      {
        accessorKey: "exhibition.expositionsDescriptionAnnee",
        header: t("expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.exhibition.expositionsDescriptionAnnee}
            />
          );
        },
      },
      {
        accessorKey: "exhibition.expositionsDescriptionTitre",
        header: t("expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return (
            <HtmlCell
              value={row.original.exhibition.expositionsDescriptionTitre}
            />
          );
        },
      },
      {
        accessorKey: "exhibition.expositionsDescriptionDates",
        header: t("expositionsDescriptionDates"),
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.exhibition.expositionsDescriptionDates}
            />
          );
        },
      },
      {
        accessorKey: "exhibition.expositionsDescriptionLieu",
        header: t("expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.exhibition.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.exhibition.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "expositionsDansEstampesMultiplesDescriptionNumero",
        header: t("expositionsDansEstampesMultiplesDescriptionNumero"),
        cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => {
          return (
            <TextCell
              value={
                row.original.expositionsDansEstampesMultiplesDescriptionNumero
              }
            />
          );
        },
      },
      {
        accessorKey:
          "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
        header: t(
          "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
        ),
        cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => {
          return (
            <TextCell
              value={
                row.original
                  .expositionsDansEstampesMultiplesDescriptionExemplaireExpose
              }
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<MultipleprintInExhibition> }) => {
          const data = row.original as MultipleprintInExhibition;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <MultipleprintInExhibitionDrawer
                key={row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "exhibition" && (
                <ShowElements
                  type="multipleprint"
                  id={row.original.multipleprint.id}
                  elements={[
                    {
                      label: t(
                        "estampesMultiplesDescriptionNumeroEnregistrement",
                      ),
                      value:
                        row.original.multipleprint
                          .estampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("estampesMultiplesDescriptionResume"),
                      value:
                        row.original.multipleprint
                          .estampesMultiplesDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.multipleprint.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "multipleprint" && (
                <ShowElements
                  type="exhibition"
                  id={row.original.exhibition.id}
                  elements={[
                    {
                      label: t("expositionsDescriptionNumeroExpo"),
                      value:
                        row.original.exhibition
                          .expositionsDescriptionNumeroExpo,
                      type: "text",
                    },
                    {
                      label: t("expositionsDescriptionResume"),
                      value:
                        row.original.exhibition.expositionsDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">{t("linkedExhibitions")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <MultipleprintToExhibitionLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        multipleprint={multipleprint}
        isAddMode={isAddMode}
        columnsLink={columns}
        searchContent={searchContent}
      />
    </div>
  );
};

const MultipleprintToExhibitionLinks = ({
  id,
  relatedData,
  fields,
  multipleprint,
  isAddMode,
  columnsLink,
  searchContent,
}: MultipleprintInExhibitionLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const { data: notAlreadyRelatedExhibitions } =
    useGetNotRelatedResource<Exhibition>({
      resultType: "exhibition",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Exhibition;
          if (!multipleprint) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="exhibition"
                id={row.original.id}
                elements={[
                  {
                    label: t("expositionsDescriptionNumeroExpo"),
                    value: row.original.expositionsDescriptionNumeroExpo,
                    type: "text",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <MultipleprintInExhibitionDrawer
                key={row.original.id}
                fields={fields}
                queryId={id}
                type={"multipleprint"}
                data={{
                  id: "",
                  exhibition: data,
                  multipleprint: multipleprint,
                  expositionsDansEstampesMultiplesDescriptionNumero: "",
                  expositionsDansEstampesMultiplesDescriptionExemplaireExpose:
                    "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [fields, id, multipleprint, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsExhibitions}
          data={notAlreadyRelatedExhibitions?.data ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: totalSize,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: totalSize,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsExhibitions,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelatedExhibitions?.data,
      relatedData,
      totalSize,
    ],
  );

  return datatableConf;
};

export default MultipleprintInExhibtionLinks;
